/** Which fiscal year a filing is about, and the regulator's dates the company can only watch for.
 *
 * Both arrive on the obligation (`reporting_year`, `watch_for`) and on the submission pack, read
 * from the regime's own rows (`regime_reporting_year_rule`, a `watch_for` deadline row). **Nothing
 * here is typed by a screen**: the sentence, the paragraph and the dates are the server's, and a
 * regime that holds no year rule prints nothing, which is "no rule read" and never "any year".
 *
 * The year is never guessed. When the company's fiscal year end is not recorded the server answers
 * `undetermined` and this prints that, with the reason, rather than a year.
 *
 * A watch-for date is the regulator's act. It is printed as "Watch for", carries no countdown, and
 * states no amount, because none is established.
 */

import type { components } from "../api/schema";
import { shortDate } from "../lib/format";
import styles from "./AlternativesNote.module.css";

export type ReportingYear = components["schemas"]["ReportingYearOut"];
export type WatchFor = components["schemas"]["WatchForOut"];

export function ReportingYearNote({
  year,
  testId = "reporting-year",
}: {
  year: ReportingYear | null | undefined;
  testId?: string;
}) {
  if (!year) return null;
  const alternative = year.alternative;
  return (
    <span className={styles.note} data-testid={testId}>
      {year.undetermined || !year.period_start || !year.period_end ? (
        <>
          <strong>Your reporting year: not determined,</strong> because {year.because}
        </>
      ) : (
        <>
          <strong>
            Your reporting year: {shortDate(year.period_start)} to {shortDate(year.period_end)},
          </strong>{" "}
          because {year.because}
        </>
      )}{" "}
      <a href={year.source_url} target="_blank" rel="noreferrer">
        {year.citation}
      </a>
      {year.filing_matches === false ? (
        <span data-testid={`${testId}-mismatch`}>
          {" "}
          This filing is for a different year than the one the rule points to.
        </span>
      ) : null}
      {alternative ? (
        <span data-testid={`${testId}-alternative`}> {alternative.because}</span>
      ) : null}
      {year.limitation ? <span data-testid={`${testId}-limitation`}> {year.limitation}</span> : null}
    </span>
  );
}

export function WatchForNote({
  items,
  testId = "watch-for",
}: {
  items: readonly WatchFor[] | undefined;
  testId?: string;
}) {
  if (!items || items.length === 0) return null;
  return (
    <span className={styles.note} data-testid={testId}>
      <strong>Watch for (not a deadline for you):</strong>
      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item.key} data-key={item.key}>
            {item.label}, by {shortDate(item.expected_by)}.{" "}
            <a href={item.source_url} target="_blank" rel="noreferrer">
              {item.citation}
            </a>
            {item.limitation ? <> {item.limitation}</> : null}
          </li>
        ))}
      </ul>
    </span>
  );
}
